import { useContext } from 'react'
import { useTranslation } from 'react-i18next'
import ConnStateUnknown from '@assets/images/common/conn-state-unknown.ico'
import { FormInput } from '@microservices/wiskey-react-components'
import { Delete } from '@mui/icons-material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  IconButton,
  Typography,
} from '@mui/material'

import { Actions } from '@pages/WindowsCreateOrEdit/components/Action'
import { useTab } from '@pages/WindowsCreateOrEdit/components/Tab/hooks/useTab'
import { WindowsCreateOrEditContext } from '@pages/WindowsCreateOrEdit/WindowsCreateOrEdit'

import { PageContentLayout } from '@layouts/PageContentLayout'
import { CustomSvgIcon } from '@components/CustomSvgIcon'
import { InputWithListVariables } from '@components/InputWithListVariables'

import { GENERATOR_INPUT_TYPE } from '@constants'

type TabProps = {
  sectionIndex: number
  tabIndex: number
  removeTab?: (index: number) => void
}

export const Tab = ({ sectionIndex, tabIndex = 0, removeTab }: TabProps) => {
  const { t } = useTranslation()
  const { state, data } = useTab(sectionIndex, tabIndex)

  const {
    watchTabTitle,
    objectsValues,
    loadingAllObjects,
    entityTypeOptions,
    entityOptions,
    isLoadingViews,
    isLoadingForms,
  } = data

  const { windowId } = useContext(WindowsCreateOrEditContext)

  const { errors, setValue } = state

  const entityName = `sections.${sectionIndex}.tabs.${tabIndex}.entity`

  return (
    <PageContentLayout sx={{ p: 1 }}>
      <Accordion>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            my: 0,
            minHeight: '48px !important',
            '& .MuiAccordionSummary-content': {
              m: '0px !important',
            },
          }}
        >
          <Typography alignSelf={'center'} flexGrow={1}>
            {t('window.tab.title')} {tabIndex + 1} | {watchTabTitle}
            {errors?.sections?.[sectionIndex]?.tabs?.[tabIndex] && (
              <CustomSvgIcon src={ConnStateUnknown} sx={{ width: 24, height: 26 }} />
            )}
          </Typography>
          <IconButton
            onClick={() => {
              if (removeTab) {
                removeTab(tabIndex)
              }
            }}
          >
            <Delete />
          </IconButton>
        </AccordionSummary>
        <AccordionDetails>
          <Box
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              width: '100%',
              p: 1,
              backgroundColor: theme => theme.palette.grey['200'],
            }}
          >
            <FormInput
              inputType={GENERATOR_INPUT_TYPE.INPUT}
              label={`${t('window.tab.label')}`}
              name={`sections.${sectionIndex}.tabs.${tabIndex}.title`}
              placeholder={t('window.tab.placeholder')}
              sx={{ width: '100%' }}
              rules={{
                required: true,
                validate: value => value.trim().length !== 0,
              }}
            />
            <FormInput
              disableClearable
              autocompleteOptions={objectsValues}
              inputType={GENERATOR_INPUT_TYPE.AUTOCOMPLETE}
              label={`${t('window.tab.object.label')}`}
              loading={loadingAllObjects}
              name={`sections.${sectionIndex}.tabs.${tabIndex}.object`}
              sx={{ width: '100%' }}
              rules={{
                required: true,
              }}
              onChangeAutocomplete={value => setValue(entityName, null)}
            />
            <FormInput
              disableClearable
              autocompleteOptions={entityTypeOptions}
              inputType={GENERATOR_INPUT_TYPE.AUTOCOMPLETE}
              label={`${t('window.tab.entityType.label')}`}
              name={`sections.${sectionIndex}.tabs.${tabIndex}.entityType`}
              sx={{ width: '100%' }}
              rules={{
                required: true,
              }}
              onChangeAutocomplete={value => setValue(entityName, null)}
            />
            <FormInput
              disableClearable
              autocompleteOptions={entityOptions}
              inputType={GENERATOR_INPUT_TYPE.AUTOCOMPLETE}
              label={`${t('window.tab.entity.label')}`}
              loading={isLoadingViews || isLoadingForms}
              name={entityName}
              sx={{ width: '100%' }}
              rules={{
                required: true,
              }}
            />
            <InputWithListVariables
              label={t('window.tab.conditions.label')}
              name={`sections.${sectionIndex}.tabs.${tabIndex}.conditions`}
              windowId={windowId}
            />
            <Actions sectionIndex={sectionIndex} tabIndex={tabIndex} />
          </Box>
        </AccordionDetails>
      </Accordion>
    </PageContentLayout>
  )
}
